import MainMenu from '@/views/MainMenu.vue'
import type { RouteRecordRaw } from 'vue-router'

const gameRoute: RouteRecordRaw = {
  path: '/game',
  name: 'game',
  component: MainMenu,
  meta: {
    menuTitle: 'Game',
  },
  children: [
    {
      path: 'classes-and-jobs',
      name: 'classes-and-jobs',
      component: () => import('@/views/game/ClassesJobs/ClassesJobsList.vue'),
      meta: {
        menuTitle: 'Classes & Jobs',
        subTitle: 'Each class has their own story to tell',
        banners: ['explore-maplestory', 'quick-start', 'world-map'],
        seo: {
          description:
            'Each character and class has their own questline and tale to tell - try them all to get the full story!',
          keywords:
            'maplestory classes, maplestory characters, maplestory jobs, maplestory class guide, maplestory best class, maplestory easiest class, gms class guide',
        },
        gaPageType: 'heroes - all',
      },
    },
    {
      path: 'classes-and-jobs/:classID/:className/:seo?',
      name: 'classes-and-jobs-detail',
      component: () => import('@/views/game/ClassesJobs/ClassesJobsDetail.vue'),
      meta: {
        menuTitle: 'Classes & Jobs',
        hideOnMenu: true,
        fullPage: true,
        banners: ['explore-maplestory', 'quick-start', 'world-map'],
        seo: {
          description:
            'Each character and class has their own questline and tale to tell - try them all to get the full story!',
        },
        gaPageType: 'heroes - detail',
        noscroll: true,
      },
    },
    {
      path: '6th-job-skills',
      name: 'sixth-job-skills',
      component: () => import('@/views/game/6thJobSkills/6thJobSkills.vue'),
      meta: {
        menuTitle: '6th Job Skills',
        subTitle: 'unlock your maximum potential with 6th job',
        banners: ['classes-and-jobs', 'news', 'maple-guides'],
        seo: {
          title: '6th Job',
          description: "Unlock your character's maximum potential with powerful 6th Job skills!",
          keywords:
            'maplestory 6th job, gms 6th job skills, maplestory hexa matrix, hexa matrix, 6th job advancement, 6th job guide, 6th job skill videos',
        },
        gaPageType: 'heroes - 6th job',
      },
    },
    {
      path: '',
      name: 'em-mw-qs',
      component: () => import('@/views/game/ExploreMaplestoryMenu.vue'),
      meta: {
        gaPageType: 'introduction',
      },
      children: [
        {
          path: 'explore-maplestory',
          name: 'explore-maplestory',
          component: () => import('@/views/game/ExploreMaplestory.vue'),
          meta: {
            menuTitle: 'Explore MapleStory',
            subTitle: "explore maple world's rich history",
            banners: ['quick-start', 'world-map', 'classes-and-jobs'],
            seo: {
              title: 'Explore',
              description: 'Explore the stories, bosses, and heroes of Maple World and beyond!',
              keywords:
                'maplestory overview, maple world overview, history of maple world, maplestory explorers, maplestory black mage, maplestory kaling, maplestory seren',
            },
          },
        },
        {
          path: 'world-map/:continent?',
          name: 'world-map',
          component: () => import('@/views/game/WorldMap.vue'),
          meta: {
            menuTitle: 'World Map',
            subTitle: 'Immerse yourself in diverse landscapes',
            banners: ['explore-maplestory', 'quick-start', 'classes-and-jobs'],
            seo: {
              description: 'From floating cities to giant submarines, there are so many diverse landscapes to explore!',
              keywords:
                'maple world map, gms world map, grandis map, arcane river map, gms maple world, maplestory maps, maplestory continents',
            },
          },
        },
        {
          path: 'quick-start',
          name: 'quick-start',
          component: () => import('@/views/game/QuickStart.vue'),
          meta: {
            menuTitle: 'Quick Start',
            subTitle: 'welcome to maplestory',
            banners: ['explore-maplestory', 'world-map', 'classes-and-jobs'],
            seo: {
              description: 'New to MapleStory? Check out this guide to get you started!',
              keywords:
                'how to play maple, how to play maplestory, maplestory beginner guide, getting started maplestory, maplestory UI explained, maplestory UI guide, how to start maplestory',
            },
          },
        },
      ],
    },
    {
      path: 'maple-guides/:category?',
      name: 'maple-guides',
      component: () => import('@/views/game/MapleGuide/index.vue'),
      redirect: { name: 'maple-guide-list' },
      meta: {
        menuTitle: 'Maple Guides',
        subTitle: 'quick courses in gameplay elements',
        banners: ['news', 'classes-and-jobs', 'player-ranking'],
        seo: {
          title: 'Guides',
          description: 'Check out the Maple Guides for tips and quick courses in different elements of gameplay!',
        },
        gaPageType: 'information',
      },
      children: [
        {
          path: '',
          name: 'maple-guide-list',
          component: () => import('@/views/game/MapleGuide/GuideList.vue'),
          meta: {
            seo: {
              keywords:
                'maplestory guide, maplestory glossary, returning mapler guide, gms maple guides, maplestory link skills, maplestory 5th job, maplestory v matrix',
            },
          },
        },
        {
          path: ':id(\\d+)/:seoTitle?',
          name: 'maple-guide-detail',
          component: () => import('@/views/game/MapleGuide/GuideDetail.vue'),
        },
      ],
    },
    {
      path: 'random-box-rates',
      name: 'random-box-rates',
      component: () => import('@/views/news/NewsDetail.vue'),
      meta: {
        menuTitle: 'Random Box Rates',
        cdnApiPath: `/general-posts/${import.meta.env.VITE_APP_CDN_HOST === 'test.g.nexonstatic.com' ? 3982 : 5805}`,
        banners: ['news', 'player-ranking', 'maple-guides'],
        seo: {
          title: 'Box Rates',
          description:
            'Look up the reward rates for different types of boxes that give you a surprise reward from a master list!',
          keywords:
            'maplestory cash shop rates, gms cash shop rates, gms probability rates, gms pssb rates, maplestory gachapon reward rates, maplestory gachapon, maplestory philosophers books',
        },
        gaPageType: 'information',
      },
    },
    {
      path: '__',
      name: 'wiki',
      redirect: '//maplestorywiki.net/w/MapleStory_Wiki',
      meta: {
        menuTitle: 'Wiki',
      },
    },
  ],
}

export default gameRoute
